import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ProductCard from '@/components/base/ProductCard';
import { products, categoryInfo } from '@/mocks/products';
import type { Product } from '@/mocks/products';

type CategoryKey = 'business' | 'festival' | 'family';

export default function Categories() {
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as CategoryKey) || 'business';
  const [activeTab, setActiveTab] = useState<CategoryKey>(initialTab);

  useEffect(() => {
    const tab = searchParams.get('tab') as CategoryKey;
    if (tab && ['business', 'festival', 'family'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const filteredProducts: Product[] = products.filter((p) => p.category === activeTab);
  const currentCategory = categoryInfo[activeTab];

  const tabs: { key: CategoryKey; label: string; icon: string }[] = [
    { key: 'business', label: '商务送礼', icon: 'ri-briefcase-4-line' },
    { key: 'festival', label: '节日礼盒', icon: 'ri-gift-2-line' },
    { key: 'family', label: '家庭享用', icon: 'ri-home-heart-line' },
  ];

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '产品分类' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-16 md:pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-10 md:py-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                产品分类
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                根据您的需求选择最合适的鲍鱼礼盒，每一款都精心搭配，诚意满满
              </p>
            </div>

            {/* Tab Switcher */}
            <div className="flex justify-center mb-10 md:mb-14">
              <div className="inline-flex bg-background-100 rounded-full p-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeTab === tab.key
                        ? 'bg-primary-500 text-background-50 shadow-sm'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={tab.icon}></i>
                    </div>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Category Description */}
            <div className="text-center mb-10 md:mb-14">
              <p className="text-sm md:text-base text-foreground-600">
                {currentCategory.description}
              </p>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-product-shop>
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无产品</p>
              </div>
            )}
          </div>
        </section>
      </div>

      <Footer />
      <ScrollToTop />
    </div>
  );
}